import React from 'react';
import { motion } from 'framer-motion';
import { UserIcon, HomeIcon, UsersIcon, IdCardIcon, VolleyballIcon, MoreHorizontalIcon, SmartphoneIcon, MessageSquareIcon, MailIcon, PhoneIcon, MonitorIcon, Share2Icon, ServerIcon, FileTextIcon, BarChart3Icon, CheckIcon, XIcon, LandmarkIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { RESIDENT_PHOTO, challengeIntro, personCard, challengeChannels, problemCards, outcomes, evidence, transition, type Bi, type StatusTone } from '../../data/communicationsChallenge';
const EASE = [0.23, 1, 0.32, 1] as const;
const REL_ICONS = {
  citizen: UserIcon,
  owner: HomeIcon,
  family: UsersIcon,
  subscriber: IdCardIcon,
  leisure: VolleyballIcon
} as const;
const CH_ICONS = {
  push: SmartphoneIcon,
  sms: MessageSquareIcon,
  email: MailIcon,
  phone: PhoneIcon,
  web: MonitorIcon,
  social: Share2Icon
} as const;
const PB_ICONS = {
  systems: ServerIcon,
  messages: FileTextIcon,
  tracking: BarChart3Icon
} as const;
const OUT_ICONS = {
  push: SmartphoneIcon,
  email: MailIcon,
  phone: PhoneIcon,
  sms: MessageSquareIcon
} as const;
const EV_ICONS = {
  channels: Share2Icon,
  citizens: LandmarkIcon,
  consumers: UserIcon
} as const;
const TONES: Record<StatusTone, {
  pill: string;
  dot: string;
}> = {
  green: {
    pill: 'bg-[#EAF7EE] text-[#1E8A45]',
    dot: 'bg-[#22A04F]'
  },
  amber: {
    pill: 'bg-[#FFF3E4] text-[#D9730D]',
    dot: 'bg-[#F08C1A]'
  },
  red: {
    pill: 'bg-[#FDECEC] text-[#D93636]',
    dot: 'bg-[#E23E3E]'
  }
};

// Connector palette — deliberately mixed to read as fragmentation.
const C = {
  violet: '#8B7FF7',
  green: '#4FCB8A',
  orange: '#F5A04A',
  red: '#F06A6A',
  blue: '#7AA7F5'
};

// Row centres (percent of column height) for evenly distributed rows.
const rows = (n: number) => Array.from({
  length: n
}, (_, i) => (i + 0.5) / n * 100);
const CH_Y = rows(6);
const PB_Y = rows(3);
const OUT_Y = [14, 38, 62, 86];
function curve(x1: number, y1: number, x2: number, y2: number) {
  const mx = (x1 + x2) / 2;
  return `M${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}
function Connectors({
  paths
}: {
  paths: {
    d: string;
    color: string;
  }[];
}) {
  return <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-2267" data-mp="7uipremj192b">
      {paths.map((p, i) => <path key={i} d={p.d} fill="none" stroke={p.color} strokeWidth="1.3" strokeDasharray="3 4" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.9" data-id="element-2268" data-mp="2wh9f1ghjnuj" data-mpi={i} />)}
    </svg>;
}

// Person → channels (all fan out from the phone area).
const PERSON_PATHS = [{
  from: 30,
  color: C.violet
}, {
  from: 44,
  color: C.green
}, {
  from: 48,
  color: C.orange
}, {
  from: 50,
  color: C.red
}, {
  from: 56,
  color: C.blue
}, {
  from: 60,
  color: C.violet
}].map((p, i) => ({
  d: curve(0, p.from, 100, CH_Y[i]),
  color: p.color
}));

// Channels → problem cards (overlapping, messy on purpose).
const CH_TO_PB: [number, number, string][] = [[0, 1, C.violet], [0, 0, C.blue], [1, 0, C.orange], [1, 1, C.green], [2, 1, C.orange], [2, 0, C.orange], [3, 1, C.red], [3, 2, C.violet], [4, 1, C.blue], [4, 2, C.green], [5, 2, C.orange], [5, 0, C.blue]];
const CH_PB_PATHS = CH_TO_PB.map(([c, p, color]) => ({
  d: curve(0, CH_Y[c], 100, PB_Y[p] + (c - 2.5) * 1.6),
  color
}));

// Problem cards → outcomes.
const PB_TO_OUT: [number, number, string][] = [[0, 0, C.violet], [0, 1, C.blue], [1, 1, C.green], [1, 2, C.red], [1, 0, C.orange], [2, 2, C.orange], [2, 3, C.violet], [2, 1, C.red], [1, 3, C.blue]];
const PB_OUT_PATHS = PB_TO_OUT.map(([p, o, color]) => ({
  d: curve(0, PB_Y[p] + (o - 1.5) * 2, 100, OUT_Y[o]),
  color
}));
export function CommunicationsChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const titleLines = isFR ? challengeIntro.titleLines.fr : challengeIntro.titleLines.en;
  const bodyLines = isFR ? challengeIntro.body.fr : challengeIntro.body.en;
  const person = <div className="relative h-full min-h-[320px] rounded-2xl overflow-hidden bg-[#F2F2F5]" data-id="element-2269" data-mp="2aui01m2nmijz">
      <img src={RESIDENT_PHOTO} alt={isFR ? 'Une citoyenne consulte son téléphone' : 'A resident checking her phone'} className="absolute inset-0 w-full h-full object-cover object-[72%_center]" data-id="element-2270" data-mp="2oln2evxepoy0" />
      <div className="absolute left-3 top-3 bottom-3 w-[46%] min-w-[150px] max-w-[190px] rounded-xl bg-white/95 backdrop-blur-sm shadow-[0_12px_30px_-20px_rgba(20,20,60,0.5)] px-4 py-4 flex flex-col" data-id="element-2271" data-mp="1vwzfyx0x8tiv">
        <p className="font-sans text-[15px] text-[#121212] leading-tight" data-id="element-2272" data-mp="25v5fj7z99nkk">
          {(isFR ? personCard.title.fr : personCard.title.en).map(l => <span key={l} className="block" data-id="element-2273" data-mp="20uhdzsdehoc3">
              {l}
            </span>)}
        </p>
        <ul className="mt-4 flex-1 flex flex-col justify-around gap-2" data-id="element-2274" data-mp="3h8r2qnlk1f3g">
          {personCard.relationships.map(r => {
          const Icon = REL_ICONS[r.key];
          return <li key={r.key} className="flex items-center gap-3 font-sans text-[13px] text-[#121212]" data-id="element-2275" data-mp="y2opztuic1yi">
                <Icon className="w-[17px] h-[17px] text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-2276" data-mp="1344a9jov24fu" />
                {pick(r.label)}
              </li>;
        })}
          <li className="text-[#4F46E5]" aria-label={isFR ? 'Et plus' : 'And more'} data-id="element-2277" data-mp="u2nxx6ehjs8z">
            <MoreHorizontalIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-2278" data-mp="1hsi9zlqzdcs6" />
          </li>
        </ul>
      </div>
    </div>;
  const channelList = <ul className="h-full flex flex-col gap-2" data-id="element-2279" data-mp="181nlsbu6c343">
      {challengeChannels.map(c => {
      const Icon = CH_ICONS[c.key];
      const t = TONES[c.tone];
      return <li key={c.key} className="flex-1 flex items-center gap-3 rounded-xl bg-white/80 px-3 py-2 min-h-[48px]" data-id="element-2280" data-mp="1cymfwudu8a2a">
            <span className="w-9 h-9 rounded-full bg-[#F4F3FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2281" data-mp="25h1hw064bnjq">
              <Icon className="w-[17px] h-[17px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-2282" data-mp="2fdjqtwmas658" />
            </span>
            <span className="flex-1 font-sans text-[13.5px] text-[#121212] whitespace-nowrap" data-id="element-2283" data-mp="2adnoe1t90mrs">{pick(c.label)}</span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-sans text-[11.5px] whitespace-nowrap ${t.pill}`} data-id="element-2284" data-mp="25dj749kino0i">
              <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} aria-hidden="true" data-id="element-2285" data-mp="20i3tniqf7u4m" />
              {pick(c.status)}
            </span>
          </li>;
    })}
    </ul>;
  const problems = <div className="h-full flex flex-col gap-3" data-id="element-2286" data-mp="1rll7qqglx6hj">
      {problemCards.map(p => {
      const Icon = PB_ICONS[p.key];
      return <div key={p.key} className="flex-1 rounded-2xl border border-[#EEEEF4] bg-white px-4 py-4 flex items-start gap-3.5 shadow-[0_12px_30px_-24px_rgba(40,36,120,0.45)]" data-id="element-2287" data-mp="1hqb27sr02gz0">
            <span className="w-11 h-11 rounded-full bg-[#F4F3FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2288" data-mp="18otr6osg95h6">
              <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2289" data-mp="3kavmawmpllad" />
            </span>
            <span className="min-w-0" data-id="element-2290" data-mp="3och3uva6unqw">
              <span className="block font-sans text-[14px] font-medium text-[#121212] leading-snug" data-id="element-2291" data-mp="3oflh0i5msttp">{pick(p.title)}</span>
              <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug mt-1" data-id="element-2292" data-mp="26adu9nbhpie">{pick(p.body)}</span>
            </span>
          </div>;
    })}
    </div>;
  const results = <div className="h-full rounded-2xl bg-[#F6F5FD] border border-[#EEEDF8] p-3 flex flex-col gap-2.5" data-id="element-2293" data-mp="35n0r99l8b4u4">
      {outcomes.map(o => {
      const Icon = OUT_ICONS[o.key];
      return <div key={o.key} className="flex-1 rounded-xl bg-white px-4 py-3 flex items-center gap-3.5 shadow-[0_8px_20px_-18px_rgba(40,36,120,0.45)]" data-id="element-2294" data-mp="3akaqti2c0wvo">
            <Icon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-2295" data-mp="2hwn7e6csscam" />
            <span className="flex-1 min-w-0" data-id="element-2296" data-mp="2mv88s93sd20n">
              <span className="block font-sans text-[13.5px] text-[#121212]" data-id="element-2297" data-mp="1g9770199wyyw">{pick(o.title)}</span>
              <span className="block font-sans text-[12px] text-[#5B6478] mt-0.5" data-id="element-2298" data-mp="1ba416elgyopv">{pick(o.sub)}</span>
            </span>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${o.ok ? 'bg-[#E6F6EC]' : 'bg-[#FDECEC]'}`} aria-label={o.ok ? isFR ? 'Réussi' : 'Succeeded' : isFR ? 'Échoué' : 'Failed'} data-id="element-2299" data-mp="1paetlm2fgbd5">
              {o.ok ? <span className="w-5 h-5 rounded-full bg-[#22A04F] flex items-center justify-center" data-id="element-2300" data-mp="1u987vgpjg6ed">
                  <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" data-id="element-2301" data-mp="2804wyxk9a5go" />
                </span> : <XIcon className="w-4 h-4 text-[#E23E3E]" strokeWidth={2.2} aria-hidden="true" data-id="element-2302" data-mp="2402tciuyn1yr" />}
            </span>
          </div>;
    })}
    </div>;
  return <div className="relative max-w-[1600px] mx-auto" data-id="element-2303" data-mp="2d0iwfr7sk733">
      <div className="pointer-events-none absolute -left-40 top-40 w-[460px] h-[460px] rounded-full bg-[#F1F0FD] opacity-70 blur-[120px]" aria-hidden="true" data-id="element-2304" data-mp="2cxmy41s17ge4" />
      <div className="pointer-events-none absolute -right-32 top-10 w-[420px] h-[420px] rounded-full bg-[#F1F0FD] opacity-70 blur-[120px]" aria-hidden="true" data-id="element-2305" data-mp="31j1hu31njq3x" />

      {/* Intro */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative text-center max-w-4xl mx-auto" data-id="element-2306" data-mp="2rldguvdgdmdb">
        <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2307" data-mp="23wuawmhvlloe">{pick(challengeIntro.eyebrow)}</p>
        <h2 className="font-serif text-[2.2rem] sm:text-5xl lg:text-[3.6rem] text-[#121212] leading-[1.06] tracking-tight mt-5" data-id="element-2308" data-mp="28wqckf33p10s">
          {titleLines.map(l => <span key={l} className="block" data-id="element-2309" data-mp="1yx9bkaysnbb5">
              {l}
            </span>)}
        </h2>
        <p className="font-sans text-[16px] md:text-[17px] text-[#5B6478] leading-relaxed mt-5" data-id="element-2310" data-mp="rjvmyymc4k8">
          {bodyLines.map(l => <span key={l} className="block" data-id="element-2311" data-mp="5rzc3ze75s6f">
              {l}
            </span>)}
        </p>
      </motion.div>

      {/* Desktop fragmentation diagram */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      delay: 0.06,
      ease: EASE
    }} className="relative hidden lg:grid mt-12 h-[400px] grid-cols-[minmax(0,1.2fr)_56px_minmax(0,1fr)_minmax(40px,90px)_minmax(0,1fr)_40px_minmax(0,1fr)] items-stretch" data-id="element-2312" data-mp="3rxuuqpk2iv8x">
        {person}
        <Connectors paths={PERSON_PATHS} data-id="element-2313" data-mp="eqt1b4p77g9y" />
        {channelList}
        <Connectors paths={CH_PB_PATHS} data-id="element-2314" data-mp="12hgkim4kdsbt" />
        {problems}
        <Connectors paths={PB_OUT_PATHS} data-id="element-2315" data-mp="12g5jgu2i6yoe" />
        {results}
      </motion.div>

      {/* Mobile / tablet stack */}
      <div className="relative lg:hidden mt-10 space-y-6" data-id="element-2316" data-mp="17a9ov8w4eynd">
        <div className="h-[340px]" data-id="element-2317" data-mp="2rxiz3ipwxmmg">{person}</div>
        {channelList}
        {problems}
        {results}
      </div>

      {/* Evidence */}
      <div className="relative mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5" data-id="element-2318" data-mp="2mxn2w5ngizhg">
        {evidence.map((e, i) => {
        const Icon = EV_ICONS[e.key];
        return <motion.article key={e.key} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.25,
          delay: i * 0.05,
          ease: EASE
        }} className="rounded-2xl border border-[#EEEEF4] bg-white px-6 py-6 flex gap-5" data-id="element-2319" data-mp="2wq2jayj3vg08" data-mpi={i}>
              <span className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2320" data-mp="2dx86ob8yfzz6">
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2321" data-mp="3pe85zqtpzf2p" />
              </span>
              <div className="border-l border-[#EEEEF4] pl-5 flex flex-col min-w-0" data-id="element-2322" data-mp="c0ojgqhfkk7">
                <p className="font-serif text-[2.8rem] text-[#4F46E5] leading-none" data-id="element-2323" data-mp="e4tm204apm2">{pick(e.value)}</p>
                <p className="font-sans text-[11.5px] font-semibold tracking-[0.2em] uppercase text-[#121212] mt-2" data-id="element-2324" data-mp="a9yc020ipdbr">{pick(e.label)}</p>
                <p className="font-sans text-[14px] text-[#5B6478] leading-relaxed mt-3" data-id="element-2325" data-mp="f9avccs6k29l">{pick(e.body)}</p>
                <a href={e.url} target="_blank" rel="noopener noreferrer" className="mt-auto pt-3 inline-flex items-center gap-2 self-start font-sans text-[13.5px] text-[#121212] transition-colors duration-150 hover:text-[#4F46E5]" data-id="element-2326" data-mp="3drpy4zrz3321">
                  {e.source}
                  <ExternalLinkIcon className="w-4 h-4" strokeWidth={1.6} aria-hidden="true" data-id="element-2327" data-mp="2v06woxdljx24" />
                </a>
              </div>
            </motion.article>;
      })}
      </div>

      {/* Transition */}
      <div className="relative text-center max-w-3xl mx-auto mt-16 lg:mt-20" data-id="element-2328" data-mp="1h3vwr855ocg7">
        <p className="font-sans text-[15px] text-[#5B6478]" data-id="element-2329" data-mp="1h1t2ud0vtz67">{pick(transition.lead)}</p>
        <p className="font-serif text-[1.6rem] md:text-[2rem] text-[#121212] leading-snug mt-3" data-id="element-2330" data-mp="1d0fwm1tvntyr">{pick(transition.main)}</p>
      </div>
    </div>;
}